import { i18n } from '@lingui/core';
import { t } from '@lingui/core/macro';
import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';

import { mutateIntegrationConnectionCreate, mutateIntegrationTokenCreate } from '@/api/integration';
import { PageSpinner } from '@/components/loaders';
import { Page } from '@/components/page';
import { InstanceNavigate } from '@/components/routing';
import { INTERNAL_OAUTH_FAILURE } from '@/error/messages/integration-standard-errors';
import SolAPIError from '@/error/SolAPIError';
import { ROUTES } from '@/router';

import SelectIntegrationOrgDialog from './SelectIntegrationOrgDialog';

const INSTANCE_ID_REGEX = /^inst:/;

const VerifyIntegration = () => {
  const { provider: providerInUrl, instance: instanceInUrl } = useParams();
  const [status, setStatus] = useState<'success' | 'error' | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [tokenId, setTokenId] = useState<string | undefined>();
  const [orgs, setOrgs] = useState<{ id: string; name: string }[] | undefined>();
  const [creatingConnection, setCreatingConnection] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const createTokenCalled = useRef(false);
  const state = searchParams.get('state');

  const stateParams = state?.split('__');
  const providerId = providerInUrl ?? stateParams?.[0];
  const instanceId = stateParams?.[1];

  const needInstanceRedirect =
    instanceId?.match(INSTANCE_ID_REGEX) &&
    instanceInUrl &&
    instanceInUrl !== instanceId.replace(INSTANCE_ID_REGEX, '');

  const handleSelectOrgId = async (orgId: string) => {
    setCreatingConnection(true);
    try {
      await mutateIntegrationConnectionCreate(tokenId!, orgId, state!);
      setStatus('success');
    } catch {
      setStatus('error');
    }
    setCreatingConnection(false);
  };

  useEffect(() => {
    const createOAuthToken = async () => {
      try {
        if (!providerId || !state) {
          throw new Error('Provider ID or state is not set');
        }

        const response = await mutateIntegrationTokenCreate(providerId, state, searchParams);

        if (response.connection?.id) {
          // There was only a single integration org and the user didn't have to select one. The
          // connection was automatically created.
          setStatus('success');
          setErrorMessage(undefined);
        } else if (response.orgs?.length) {
          // Not done yet, the user needs to choose an org associated with their integration account
          setTokenId(response.id);
          setOrgs(response.orgs);
        } else {
          // Something unexpected happened
          setStatus('error');
          setErrorMessage(i18n._(INTERNAL_OAUTH_FAILURE));
        }
      } catch (e) {
        let errMessage;
        if (e instanceof SolAPIError) {
          errMessage = e.getFirstError().message;
        } else {
          errMessage = i18n._(INTERNAL_OAUTH_FAILURE);
        }
        setStatus('error');
        setErrorMessage(errMessage);
      }
    };

    if (!needInstanceRedirect && providerId && searchParams.size && !createTokenCalled.current) {
      createTokenCalled.current = true;
      createOAuthToken();
    }
  }, [providerId, needInstanceRedirect, searchParams, state]);

  if (!searchParams.size || !providerId || !state) {
    return null;
  }

  if (instanceId && needInstanceRedirect) {
    // Force a hard refresh on this redirect to ensure the instance is set correctly in context
    // without adding to browser history.
    window.location.replace(
      ROUTES.verifyIntegrationRedirect(
        instanceId.replace(INSTANCE_ID_REGEX, ''),
        providerId,
        searchParams,
      ),
    );
    return null;
  }

  if (status != null && providerId) {
    return (
      <InstanceNavigate
        to={ROUTES.integrationById(providerId)!}
        state={{ connectionSavedSuccess: status === 'success', connectionSavedError: errorMessage }}
      />
    );
  }

  return (
    <Page
      className="flex items-start"
      title={t`Settings`}
      pageName={t`Integrations`}
      exampleAIQuestions={[]}
    >
      <PageSpinner text={t`Creating connection`} />
      <SelectIntegrationOrgDialog
        orgs={orgs}
        isLoading={creatingConnection}
        onCancel={() => setStatus('error')}
        onOk={handleSelectOrgId}
      />
    </Page>
  );
};

export default VerifyIntegration;
